import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { ApiError, endpoints, type MBAPair } from "../services/api";

const PERIOD_DAYS = 30;

type SortKey = "support" | "confidence" | "lift" | "co_occurrences";

export default function MBAPage() {
  const { t } = useTranslation();
  const { currentStoreId } = useAuth();
  const [pairs, setPairs] = useState<MBAPair[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortKey>("lift");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    if (currentStoreId === null) {
      setLoading(false);
      setPairs([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    endpoints
      .mba(currentStoreId, PERIOD_DAYS, 200)
      .then((p) => {
        if (!cancelled) setPairs(p);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : "Błąd ładowania");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentStoreId]);

  const sorted = useMemo(() => {
    if (pairs === null) return [];
    const copy = [...pairs];
    copy.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      return direction === "desc" ? bv - av : av - bv;
    });
    return copy;
  }, [pairs, sortBy, direction]);

  function toggleSort(key: SortKey) {
    if (sortBy === key) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setDirection("desc");
    }
  }

  function arrow(key: SortKey): string {
    if (sortBy !== key) return "";
    return direction === "desc" ? " ▼" : " ▲";
  }

  return (
    <Layout>
      <header className="page-header">
        <h1>{t("nav.mba")}</h1>
        <span className="page-header__hint">Okres: ostatnie {PERIOD_DAYS} dni</span>
      </header>

      <section className="card">
        <p>
          Pary spełniające progi BasketBoostera: support &gt; 5%, confidence &gt; 30%, lift &gt; 1.2.
          Sortuj klikając w nagłówki kolumn.
        </p>
      </section>

      {currentStoreId === null && (
        <section className="card empty-state">
          <p>Wybierz sklep, aby zobaczyć analizę MBA.</p>
        </section>
      )}

      {loading && (
        <section className="card">
          <p>Ładowanie…</p>
        </section>
      )}

      {error && (
        <section className="card">
          <p style={{ color: "var(--color-danger)" }}>{error}</p>
        </section>
      )}

      {!loading && !error && pairs !== null && pairs.length === 0 && (
        <section className="card empty-state">
          <h2>Brak par MBA</h2>
          <p>
            Pary „kupowane razem" wymagają raportu transakcyjnego z <strong>Nr dokumentu</strong>{" "}
            (numer paragonu) — czyli surowych pozycji per paragon. Raport „Najczęściej w koszyku"
            w formacie <em>Excel 2003 XML</em> jest agregatem per produkt — z niego policzymy KPI
            i bestsellery (zobacz Dashboard), ale nie pary.
          </p>
          <p>
            Aby wyliczyć pary, wyeksportuj raport z Symplexa w wariancie <strong>Tekstowym (CSV,
            separator ;)</strong> z kolumną „Nr dokumentu", albo poproś o dodanie obsługi innego
            raportu transakcyjnego.
          </p>
          <Link to="/import" className="btn btn--primary">Wgraj raport →</Link>
        </section>
      )}

      {!loading && !error && pairs !== null && pairs.length > 0 && (
        <section className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Jeśli kupują</th>
                <th>To biorą też</th>
                <th
                  onClick={() => toggleSort("support")}
                  title={t("metrics.support", { percent: "X" })}
                  style={{ cursor: "pointer" }}
                >
                  Support{arrow("support")}
                </th>
                <th
                  onClick={() => toggleSort("confidence")}
                  title={t("metrics.confidence", { percent: "X" })}
                  style={{ cursor: "pointer" }}
                >
                  Confidence{arrow("confidence")}
                </th>
                <th
                  onClick={() => toggleSort("lift")}
                  title={t("metrics.lift")}
                  style={{ cursor: "pointer" }}
                >
                  Lift{arrow("lift")}
                </th>
                <th
                  onClick={() => toggleSort("co_occurrences")}
                  style={{ cursor: "pointer" }}
                >
                  #{arrow("co_occurrences")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => (
                <tr key={`${p.antecedent_sku}->${p.consequent_sku}`}>
                  <td>{p.antecedent_sku}</td>
                  <td>{p.consequent_sku}</td>
                  <td>{(p.support * 100).toFixed(1)}%</td>
                  <td>{(p.confidence * 100).toFixed(1)}%</td>
                  <td>{p.lift.toFixed(2)}</td>
                  <td>{p.co_occurrences}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </Layout>
  );
}
